import { useCallback, type ComponentProps } from "react";
import { Trans, useTranslation } from "react-i18next";
import type { ParseKeys, TOptions } from "i18next";
import type { en } from "@/locales/en";
import i18n, { DEFAULT_LANGUAGE, isLanguage, type Language } from "@/lib/i18n";

// Typed translation helpers. Components use these instead of react-i18next directly.
//
// Why: this project compiles with `strict: false`. In that mode react-i18next's own
// `t(key, options)` and `<Trans i18nKey>` types collapse and reject valid calls
// (interpolation, plurals, Trans). These wrappers keep the key fully type checked
// against the English files while accepting any options.

type Namespace = keyof typeof en;

/** Any key in any namespace, always with its prefix: "holdings:table.marketValue". */
export type TranslationKey = ParseKeys<Namespace[], TOptions>;

export type TranslateFn = (key: TranslationKey, options?: TOptions) => string;

/** Returns `t`. Re-renders the component when the language changes. */
export function useT(): TranslateFn {
  const { t } = useTranslation();
  // Plain signature on purpose; the real check happens on TranslateFn's key type.
  return useCallback(
    (key: TranslationKey, options?: TOptions) =>
      (t as unknown as (key: string, options?: TOptions) => string)(
        key,
        options,
      ),
    [t],
  );
}

/**
 * Non hook `t`, always in the current language. For code that is not a component, and for
 * callbacks registered once that would otherwise keep the `t` from their first render
 * (third party SDK callbacks, listeners set up in a mount-only effect).
 */
export const translate: TranslateFn = (key, options) =>
  (i18n.t as unknown as (key: string, options?: TOptions) => string)(
    key,
    options,
  );

/**
 * The active language. Re-renders the component when it changes, so formatting
 * helpers built on it (useFormatters, useLocale) always use the current language.
 */
export function useLanguage(): Language {
  const { i18n: instance } = useTranslation();
  return isLanguage(instance.language) ? instance.language : DEFAULT_LANGUAGE;
}

/**
 * Translated text for a backend error body `{ detail, code, params? }` (see pythonbackend/api/errors.py).
 * Returns null when there is no code or no `errors:<code>` translation, so callers fall
 * back to the English `detail`: `translateApiError(body) ?? body.detail ?? fallback`.
 */
export function translateApiError(body: unknown): string | null {
  if (typeof body !== "object" || body === null) return null;
  const { code, params } = body as { code?: unknown; params?: unknown };
  if (typeof code !== "string" || code === "") return null;
  const key = `errors:${code}`;
  if (!i18n.exists(key)) return null;
  const options =
    typeof params === "object" && params !== null && !Array.isArray(params)
      ? (params as Record<string, unknown>)
      : {};
  // The key is built at runtime and checked with exists(), so it cannot be typed.
  return (i18n.t as unknown as (key: string, options: object) => string)(
    key,
    options,
  );
}

type TProps = Omit<ComponentProps<typeof Trans>, "i18nKey" | "t" | "ns"> & {
  k: TranslationKey;
};

/** Typed `<Trans>`, for sentences with embedded JSX: <T k="ns:key" components={{ ... }} />. */
export function T({ k, ...props }: TProps) {
  return <Trans i18nKey={k as never} {...props} />;
}
